/**
 * @description Página para gerar código de iframe para embedar em sistemas externos
 * @module ScheduleIntegrator
 * @since Março 2024
 */

import { useState, useEffect } from "react";
import { useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Snackbar,
  Alert,
  Grid,
  Slider,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Checkbox,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VideocamIcon from "@mui/icons-material/Videocam";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { GenericTable } from "@/components/GenericTable/GenericTable";
import { currentFeatures, oids } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetSchedule } from "@/services/fhir";
import { LocationOn } from "@mui/icons-material";

type PortalType = "fluxmed" | "fluxvet";

interface IFrameOptions {
  portalType: PortalType;
  doctor: string;
  location: string;
  columns: number;
  itemsPerPage: number;
  showFilters: boolean;
  showDateFilter: boolean;
  /** Quando true, o portal público ativa o fluxo de pagamento antecipado. */
  advancePayment: boolean;
  width: number;
  widthUnit: "%" | "px";
  height: number;
  heightUnit: "%" | "px";
  selectedScheduleIds: string[];
}

export function ScheduleIntegrator() {
  // Determina o tipo de portal baseado nas features da aplicação
  // Se defaultApp for true, usa fluxmed; caso contrário, usa fluxvet
  const { organization } = useOrganizationContext();
  const { access } = useSessionContext();
  const portalType: PortalType = currentFeatures.defaultApp
    ? "fluxmed"
    : "fluxvet";

  const [options, setOptions] = useState<IFrameOptions>({
    portalType,
    doctor: "",
    location: "",
    columns: 2,
    itemsPerPage: 10,
    showFilters: true, // Padrão: exibe filtros (mesmo comportamento do PractitionerCards)
    showDateFilter: false,
    advancePayment: false,
    width: 100,
    widthUnit: "%",
    height: 800,
    heightUnit: "px",
    selectedScheduleIds: [],
  });
  const [showSnackbar, setShowSnackbar] = useState(false);

  // Estado para armazenar a lista de schedules disponíveis
  const [schedules, setSchedules] = useState<
    fhir5.BundleEntry<fhir5.Schedule>[]
  >([]);
  // Mapa scheduleId -> Location (para verificar se é virtual ou presencial)
  const [locationsMap, setLocationsMap] = useState<Map<string, fhir5.Location>>(
    new Map(),
  );
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  // Estado para controlar qual tipo de código está sendo exibido (0 = URL, 1 = Iframe)
  const [codeType, setCodeType] = useState<0 | 1>(0);

  // Busca a lista de schedules disponíveis ao montar o componente
  // Inclui includeLocation para obter dados da Location (virtual/presencial)
  useEffect(() => {
    async function fetchSchedules() {
      if (!access?.organizationId) return;

      setLoadingSchedules(true);
      try {
        const scheduleResponse = await GetSchedule({
          active: true,
          organization: `${oids.cnpj}-${access.organizationId}`,
          includeLocation: true,
          count: 250,
        });

        if (scheduleResponse?.entry) {
          setSchedules(scheduleResponse.entry);

          // Extrai Locations da resposta (incluídas via _include)
          const locationsById = new Map<string, fhir5.Location>();
          scheduleResponse.entry.forEach((entry: any) => {
            if (entry?.resource?.resourceType === "Location") {
              const loc = entry.resource as fhir5.Location;
              if (loc.id) locationsById.set(loc.id, loc);
            }
          });

          // Mapeia scheduleId -> Location via actor reference
          const scheduleToLocation = new Map<string, fhir5.Location>();
          scheduleResponse.entry.forEach((entry: any) => {
            if (
              entry?.resource?.resourceType === "Schedule" &&
              entry?.resource?.id
            ) {
              const schedule = entry.resource as fhir5.Schedule;
              const locationActor = schedule.actor?.find((a) =>
                a.reference?.startsWith("Location/"),
              );
              if (locationActor?.reference) {
                const locationId = locationActor.reference.split("/")[1];
                const location = locationsById.get(locationId);
                if (location) {
                  scheduleToLocation.set(schedule.id!, location);
                }
              }
            }
          });
          setLocationsMap(scheduleToLocation);
        }
      } catch (error) {
        console.error("Erro ao buscar schedules:", error);
      } finally {
        setLoadingSchedules(false);
      }
    }

    fetchSchedules();
  }, [access?.organizationId]);

  // Verifica se Location é virtual (teleatendimento) via extensão
  const isVirtualLocation = (loc: fhir5.Location | undefined): boolean => {
    if (!loc) return false;
    const ext = loc.extension?.find(
      (e) =>
        e.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service",
    );
    return ext?.valueBoolean === true;
  };

  // Função auxiliar para extrair informações do schedule
  const getScheduleInfo = (
    schedule: fhir5.Schedule,
    locationResource?: fhir5.Location,
  ) => {
    const serviceCost = schedule.extension?.find(
      (ext) =>
        ext.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/service-cost",
    )?.valueDecimal;

    const slotDuration = schedule.extension?.find(
      (ext) =>
        ext.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/schedule-slot-duration",
    )?.valueDuration?.value;

    const practitioner = schedule.actor?.find((actor) =>
      actor.reference?.startsWith("Practitioner/"),
    );

    const location = schedule.actor?.find((actor) =>
      actor.reference?.startsWith("Location/"),
    );

    const healthcareService = schedule.actor?.find((actor) =>
      actor.reference?.startsWith("HealthcareService/"),
    );

    const isVirtual = isVirtualLocation(locationResource);

    return {
      serviceCost,
      slotDuration,
      practitioner: practitioner?.display || "",
      location: location?.display || "",
      healthcareService: healthcareService?.display || "",
      isVirtual,
    };
  };

  // Removida a seleção individual de schedules:
  // o iframe passa a considerar todas as agendas ativas da organização,
  // simplificando a experiência. Mantemos apenas dados agregados para exibição,
  // caso seja necessário no futuro.

  // Gera a URL com os parâmetros selecionados
  const generateUrl = () => {
    const params = new URLSearchParams();

    if (options.columns !== 3)
      params.append("columns", options.columns.toString());
    if (options.itemsPerPage !== 10)
      params.append("itemsPerPage", options.itemsPerPage.toString());

    // Adiciona o parâmetro organization se disponível
    // Este parâmetro é usado no PractitionerCards para filtrar profissionais por organização
    if (organization?.id) {
      params.append("organization", organization.id);
    }

    // Adiciona o parâmetro showFilters apenas se for false (padrão é true no PractitionerCards)
    // Quando showFilters é false, os filtros não são exibidos e todos os profissionais são mostrados diretamente
    // Se for true (padrão), não precisa incluir o parâmetro na URL
    if (!options.showFilters) {
      params.append("showFilters", "false");
    }

    // Pagamento antecipado: só inclui quando ativado para manter compatibilidade
    // com integrações antigas (sem este parâmetro).
    if (options.advancePayment) {
      params.append("advancePayment", "true");
    }

    // Removido o parâmetro "schedules": o iframe passa a considerar
    // todas as agendas ativas da organização, sem seleção manual.

    const appEnvironment =
      window.location.origin?.includes("dev") ||
      window.location.origin?.includes("localhost")
        ? "dev"
        : "app";
    const baseUrl = `https://${appEnvironment}.${options.portalType}.com.br/portal/#/available-practitioners`;
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  // Gera o código do iframe com base nas opções selecionadas
  const generateIframeCode = () => {
    return `<iframe
  src="${generateUrl()}"
  title="Profissionais Disponíveis - Fluxmed"
  style="width: ${options.width}${options.widthUnit}; height: ${
    options.height
  }${options.heightUnit}; border: none; overflow: hidden;"
  allow="fullscreen"
></iframe>`;
  };

  // Copia o código para a área de transferência
  const handleCopyCode = () => {
    navigator.clipboard.writeText(generateIframeCode());
    setShowSnackbar(true);
  };

  // Abre a URL em uma nova janela (popup) para visualização
  // Sempre abre com 800px de largura e centralizada na tela
  const handleOpenPreview = () => {
    // Dimensões fixas da janela popup
    const width = 800;
    const height = 800; // Altura padrão de 800px

    // Calcula a posição central da janela na tela
    const left = Math.round((window.screen.width - width) / 2);
    const top = Math.round((window.screen.height - height) / 2);

    // Opções da janela popup
    const windowFeatures = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=no,location=no,toolbar=no,menubar=no`;

    window.open(generateUrl(), "_blank", windowFeatures);
  };

  return (
    <>
      <Stack spacing={4} width="100%" alignItems="center">
        <Grid container spacing={3} width="100%">
          {/* Coluna esquerda: Configurações principais da integração */}
          <Grid item xs={12} md={6}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Stack spacing={3}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Configurações da Integração
                  </Typography>

                  {/* Layout e filtros – agora visíveis diretamente */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Layout
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Defina como os profissionais serão exibidos no portal.
                    </Typography>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2}>
                        <FormControl
                          variant="filled"
                          size="small"
                          sx={{
                            minWidth: 200,
                            "& .MuiFilledInput-root": {
                              backgroundColor: "neutral100.main",
                              "&:hover": {
                                backgroundColor: "neutral100.dark",
                              },
                            },
                          }}
                        >
                          <InputLabel id="columns-select-label">
                            Colunas
                          </InputLabel>
                          <Select
                            labelId="columns-select-label"
                            value={options.columns}
                            label="Colunas"
                            onChange={(e) =>
                              setOptions({
                                ...options,
                                columns: e.target.value as number,
                              })
                            }
                          >
                            {[1, 2, 3].map((num) => (
                              <MenuItem key={num} value={num}>
                                {num}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>

                      {/* Controle para exibir/ocultar filtros */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={options.showFilters}
                            onChange={(e) =>
                              setOptions({
                                ...options,
                                showFilters: e.target.checked,
                              })
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              Exibir filtros de busca
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {options.showFilters
                                ? "Os filtros de busca serão exibidos acima dos cards"
                                : "Os cards serão exibidos diretamente sem filtros"}
                            </Typography>
                          </Box>
                        }
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={options.advancePayment}
                            onChange={(e) =>
                              setOptions({
                                ...options,
                                advancePayment: e.target.checked,
                              })
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              Pagamento antecipado
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Quando ativado, consultas com custo podem gerar link
                              e cobrança automática via WhatsApp após a reserva.
                            </Typography>
                          </Box>
                        }
                      />
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Dimensões do iframe – abaixo das configurações de layout */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Dimensões do Iframe
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Configure a largura e altura do iframe conforme o espaço
                      disponível na sua aplicação.
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography>
                          Largura: {options.width}
                          {options.widthUnit}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={options.widthUnit}
                            onChange={(e) =>
                              setOptions({
                                ...options,
                                widthUnit: e.target.value as "%" | "px",
                              })
                            }
                            sx={{ height: 30 }}
                          >
                            <MenuItem value="%">%</MenuItem>
                            <MenuItem value="px">px</MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                      <Slider
                        value={options.width}
                        onChange={(_, newValue) =>
                          setOptions({
                            ...options,
                            width: newValue as number,
                          })
                        }
                        min={options.widthUnit === "%" ? 10 : 200}
                        max={options.widthUnit === "%" ? 100 : 2000}
                        step={options.widthUnit === "%" ? 5 : 50}
                        valueLabelDisplay="auto"
                      />
                    </Box>

                    <Box>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography>
                          Altura: {options.height}
                          {options.heightUnit}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={options.heightUnit}
                            onChange={(e) =>
                              setOptions({
                                ...options,
                                heightUnit: e.target.value as "%" | "px",
                              })
                            }
                            sx={{ height: 30 }}
                          >
                            <MenuItem value="%">%</MenuItem>
                            <MenuItem value="px">px</MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                      <Slider
                        value={options.height}
                        onChange={(_, newValue) =>
                          setOptions({
                            ...options,
                            height: newValue as number,
                          })
                        }
                        min={options.heightUnit === "%" ? 10 : 200}
                        max={options.heightUnit === "%" ? 100 : 2000}
                        step={options.heightUnit === "%" ? 5 : 50}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Coluna direita: geração do código / preview */}
          <Grid item xs={12} md={6}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Stack spacing={3}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Integração de Agenda
                  </Typography>

                  {/* Seção principal: Copiar Código */}
                  <Box
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {/* Tabs para alternar entre URL e Iframe */}
                    <Tabs
                      value={codeType}
                      onChange={(_, newValue) => setCodeType(newValue as 0 | 1)}
                      sx={{
                        mb: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                        "& .MuiTab-root": {
                          fontWeight: 500,
                        },
                      }}
                    >
                      <Tab
                        label="Código de integração - Iframe"
                        sx={{ textTransform: "none" }}
                      />
                      <Tab label="Preview" sx={{ textTransform: "none" }} />
                    </Tabs>

                    {/* Conteúdo baseado na tab selecionada */}
                    {codeType === 0 ? (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Cole este código em seu site para exibir os
                          profissionais.
                        </Typography>
                        <Stack
                          direction="row"
                          justifyContent="flex-end"
                          sx={{ mb: 1 }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyCode}
                            sx={{
                              textTransform: "none",
                              px: 3,
                              fontWeight: 600,
                            }}
                          >
                            Copiar Código
                          </Button>
                        </Stack>
                        <TextField
                          fullWidth
                          label="Código do Iframe"
                          multiline
                          rows={10}
                          value={generateIframeCode()}
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            },
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Copie a URL abaixo para testar em uma janela anônima.
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            value={generateUrl()}
                            InputProps={{
                              readOnly: true,
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: "0.9rem",
                                fontWeight: 500,
                              },
                            }}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => {
                              navigator.clipboard.writeText(generateUrl());
                              setShowSnackbar(true);
                            }}
                            sx={{
                              textTransform: "none",
                              whiteSpace: "nowrap",
                              px: 3,
                              fontWeight: 600,
                            }}
                          >
                            Copiar
                          </Button>
                        </Stack>
                        {/* 
                          Pré-visualização em iframe usando a mesma URL de teste gerada acima.
                          Esta visualização ajuda o usuário a ver rapidamente como o portal
                          será carregado, sem precisar abrir uma nova aba/janela.
                        */}
                        <Box sx={{ mt: 3 }}>
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Preview de integração
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Preview de integração com as configurações
                            selecionadas.
                          </Typography>
                          <Box
                            sx={{
                              borderRadius: 1,
                              overflow: "hidden",
                              border: "1px solid",
                              borderColor: "grey.300",
                            }}
                          >
                            <Box
                              component="iframe"
                              src={generateUrl()}
                              title="Preview - Agenda em URL de teste"
                              sx={{
                                width: "100%",
                                height: 600,
                                border: "none",
                              }}
                            />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            p: 2,

                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "info.main",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="info.dark"
                            sx={{ mb: 1, fontWeight: 500 }}
                          >
                            Como testar:
                          </Typography>
                          <Typography
                            variant="body2"
                            color="info.dark"
                            component="div"
                          >
                            <ol style={{ margin: 0, paddingLeft: 20 }}>
                              <li>Copie a URL acima</li>
                              <li>
                                Abra uma janela anônima/privada do navegador
                              </li>
                              <li>Cole a URL na barra de endereços</li>
                              <li>
                                Visualize como os profissionais serão exibidos
                              </li>
                            </ol>
                          </Typography>
                        </Box>
                      </>
                    )}

                    {/* Configurações adicionais em Accordion */}
                    <Divider sx={{ my: 3 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Código copiado com sucesso!
        </Alert>
      </Snackbar>
    </>
  );
}
